import { Package, Percent } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';

import { supabase } from '../../../lib/supabase';
import { SubscriptionWizard } from './SubscriptionWizard';
import { DiscountCodeWizard } from '../DiscountCodes/DiscountCodeWizard';

interface SubscriptionsAndDiscountsWizardProps {
  companyId: string;
  onComplete: () => void;
  onBack: () => void;
}

type View = 'menu' | 'subscriptions' | 'discounts';

export function SubscriptionsAndDiscountsWizard({ 
  companyId, 
  onComplete, 
  onBack 
}: SubscriptionsAndDiscountsWizardProps) {
  const [currentView, setCurrentView] = useState<View>('menu');
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [discountCodeCount, setDiscountCodeCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadCounts = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [subscriptionsResult, discountCodesResult] = await Promise.all([
        supabase
          .from('company_subscriptions')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId),
        supabase
          .from('discount_codes')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId),
      ]);

      setSubscriptionCount(subscriptionsResult.count || 0);
      setDiscountCodeCount(discountCodesResult.count || 0);
    } catch (err) {
      console.error('Error loading counts:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId && currentView === 'menu') {
      loadCounts();
    }
  }, [companyId, currentView, loadCounts]);

  if (currentView === 'subscriptions') {
    return (
      <SubscriptionWizard
        companyId={companyId}
        onComplete={() => {
          loadCounts();
          setCurrentView('menu');
        }}
        onBack={() => setCurrentView('menu')}
        showCompleteButton={false}
      />
    );
  }

  if (currentView === 'discounts') {
    return (
      <DiscountCodeWizard
        companyId={companyId}
        onComplete={() => {
          loadCounts();
          setCurrentView('menu');
        }}
        onBack={() => setCurrentView('menu')}
        showCompleteButton={false}
      />
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
          <Package size={32} className="text-text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Subscriptions & Discounts</h2>
        <p className="text-[#A9A3A3] mt-2">Create your membership plans</p>
      </div>
      <p className="text-[#A9A3A3] text-center mb-6">
        Set up subscription plans for your members. You can also create discount codes to offer promotions.
      </p>
      <div className="space-y-3">
        <button
          onClick={() => setCurrentView('subscriptions')}
          className="w-full py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-semibold rounded-lg transition-colors text-center flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Package size={18} />
            <span>Create Subscriptions</span>
          </div>
          {!loading && (
            <span className="text-sm text-white/70">
              {subscriptionCount === 0 ? 'None created' : `${subscriptionCount} created`}
            </span>
          )}
        </button>
        <button
          onClick={() => setCurrentView('discounts')}
          className="w-full py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors text-center flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Percent size={18} />
            <span>Create Discount Codes</span>
          </div>
          {!loading && (
            <span className="text-sm text-white/70">
              {discountCodeCount === 0 ? 'None created' : `${discountCodeCount} created`}
            </span>
          )}
        </button>
      </div>
      <div className="flex gap-4 mt-6">
        <button
          onClick={onBack}
          className="flex-1 py-3 px-4 border border-[#504A4A] hover:bg-[#363232] text-[#A9A3A3] font-semibold rounded-lg transition-colors"
        >
          Back
        </button>
        <button
          onClick={onComplete}
          className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
        >
          Complete Setup
        </button>
      </div>
    </div>
  );
}
